<template>
  <div class="relative">
    <UITable
      :columns="columns"
      :items="items"
      :loading="loading"
      :selectable="!isCheckedAllDisabled"
      :selected.sync="selectedOrderIds"
      :is-selectable-row="isSelectableRow"
      item-key="outbound_detail_id"
      class="font-medium"
      @selection-change="handleSelectionChange"
    >
      <template #body="{ totalColumnCount }">
        <!-- Loading State -->
        <tbody
          v-if="loading && (!items || items.length === 0)"
          class="divide-y divide-[#E5E5E5]"
        >
          <tr>
            <td
              :colspan="totalColumnCount"
              class="py-8 text-center"
            >
              <div class="flex items-center justify-center gap-2">
                <b-spinner
                  variant="primary"
                  label="Loading..."
                  small
                />
                <span class="text-sm text-[#737373]">Memuat data...</span>
              </div>
            </td>
          </tr>
        </tbody>

        <!-- Empty State -->
        <tbody
          v-else-if="!items || items.length === 0"
          class="divide-y divide-[#E5E5E5]"
        >
          <tr>
            <td
              :colspan="totalColumnCount"
              class="py-8 text-center text-[#737373] text-sm"
            >
              Tidak ada data yang ditampilkan
            </td>
          </tr>
        </tbody>

        <!-- Data Order Groups -->
        <template v-else>
          <tbody
            v-for="(group, gIdx) in orderGroups"
            :key="group.order.outbound_detail_id || gIdx"
            :class="[
              rowClass(group.order),
              'divide-y divide-[#E5E5E5] transition-colors border-b border-[#E5E5E5]',
            ]"
          >
            <tr
              v-for="row in group.rows"
              :key="row.rowKey"
            >
              <!-- Checkbox (Order-level) -->
              <td
                v-if="!isCheckedAllDisabled && row.orderRowspan > 0"
                :rowspan="row.orderRowspan"
                class="py-[13px] px-[16px] text-center align-middle"
              >
                <TableCheckbox
                  :disabled="!isSelectableRow(row.order)"
                  :checked="selectedOrderIds.includes(row.order.outbound_detail_id)"
                  @change="() => toggleSelectRow(row.order)"
                />
              </td>

              <!-- Order Number (Order-level) -->
              <td
                v-if="row.orderRowspan > 0"
                :rowspan="row.orderRowspan"
                class="py-[16px] px-[16px] align-middle text-[#0A0A0A] text-[14px]"
              >
                <span>{{ row.orderIndex + 1 }}</span>
              </td>

              <!-- AWB / Resi (Order-level) -->
              <td
                v-if="row.orderRowspan > 0"
                :rowspan="row.orderRowspan"
                class="py-[16px] px-[16px] align-middle text-[#0A0A0A] text-[14px]"
              >
                <span>{{ row.order.awb || row.order.order_no || '-' }}</span>
              </td>

              <!-- Product Name (Product-level) -->
              <td
                v-if="row.productRowspan > 0"
                :rowspan="row.productRowspan"
                class="py-[16px] px-[16px] align-middle text-[#0A0A0A] text-[14px]"
              >
                <span>{{ row.productName }}</span>
              </td>

              <!-- Variant Name (Variant-level) -->
              <td class="py-[16px] px-[16px] align-middle text-[#0A0A0A] text-[14px]">
                <span>{{ row.variantName }}</span>
              </td>

              <!-- Total Item / Quantity (Product-level) -->
              <td
                v-if="row.productRowspan > 0"
                :rowspan="row.productRowspan"
                class="py-[16px] px-[16px] align-middle text-[#0A0A0A] text-[14px]"
              >
                <span>{{ row.totalItem }}</span>
              </td>

              <!-- Packings (Order-level) -->
              <td
                v-if="row.orderRowspan > 0"
                :rowspan="row.orderRowspan"
                class="py-[16px] px-[16px] align-middle text-[#0A0A0A] text-[14px]"
              >
                <PackingSelection
                  v-model="row.order.packings"
                  :fetcher="fetcherPackingMaterials"
                  :disabled="isPackingDisabled(row.order)"
                  @change="() => handlePackingChange(row.order, row.orderIndex)"
                />

                <Button
                  :disabled="isPackingDisabled(row.order) || isAddPackingDisabled(row.order)"
                  text-color="primary"
                  class="p-0 !h-[20px] text-[14px] mt-[12px]"
                  type="ghost"
                  @click="handleAddPacking(row.order)"
                >
                  <span class="k-add text-[14px]" />
                  Bahan Packing
                </Button>
              </td>

              <!-- Status (Order-level) -->
              <td
                v-if="row.orderRowspan > 0"
                :rowspan="row.orderRowspan"
                class="py-[16px] px-[16px] align-middle text-[#0A0A0A] text-[14px]"
              >
                <StatusPackingPill :status="row.order.status" />
              </td>

              <!-- Fulfillment Fee (Order-level) -->
              <td
                v-if="row.orderRowspan > 0"
                :rowspan="row.orderRowspan"
                class="py-[16px] px-[16px] align-middle text-[#0A0A0A] text-[14px]"
              >
                <div
                  v-if="recalculator && recalculator.isRecalculating(row.order.outbound_detail_id)"
                  class="flex items-center gap-1 text-[#737373] text-[13px]"
                >
                  <b-spinner
                    small
                    variant="primary"
                  />
                  <span>Menghitung...</span>
                </div>
                <div
                  v-else-if="recalculator && recalculator.hasError(row.order.outbound_detail_id)"
                  class="flex items-start gap-[4px] flex-wrap"
                >
                  <span class="text-[#E31A1A] text-[12px]">Gagal menghitung, </span>
                  <Button
                    text-color="danger"
                    class="!py-[2px] !px-[6px] !text-[11px] flex items-center gap-1"
                    type="ghost"
                    @click="handleRetryRecalculate(row.order, row.orderIndex)"
                  >
                    <span class="k-refresh text-[11px]" />
                    Coba Lagi
                  </Button>
                </div>
                <span v-else>{{ (row.order.fulfillment_fee || row.order.fulfillment_fee_per_order || 0) | rupiah }}</span>
              </td>
            </tr>
          </tbody>
        </template>
      </template>
    </UITable>
  </div>
</template>

<script>
import { Table as UITable, Button } from '@/@core/components/ui'
import TableCheckbox from '@/@core/components/ui/table/TableCheckbox.vue'
import PackingSelection from './PackingSelection.vue'
import StatusPackingPill from '../StatusPackingPill.vue'
import { columnsDetailNonKomship } from './config'
import usePackingMaterialsFetcher from './composable/usePackingMaterialsFetcher'
import useRecalculateFulfillment from './composable/useRecalculateFulfillment'

export default {
  name: 'Table',
  components: {
    UITable,
    TableCheckbox,
    PackingSelection,
    StatusPackingPill,
    Button,
  },
  props: {
    batchStatus: {
      type: String,
      default: 'Belum Dipacking',
    },
    items: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: true,
    },
    packingCanBeUpdated: {
      type: Boolean,
      default: false,
    },
    warehouseCosts: {
      type: Object,
      default: () => {},
    },
    kompackRevenue: {
      type: Object,
      default: () => {},
    },
    outboundOperations: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      columns: columnsDetailNonKomship,
      selectedOrderIds: [],
      packingMaterialsFetcher: undefined,
      recalculator: null,
    }
  },
  computed: {
    isAllShipped() {
      if (this.batchStatus !== 'Dikirim') {
        return true
      }
      return this.items.some(item => item.status !== 'Dikirim' && item.status !== 'Dibatalkan')
    },
    isCheckedAllDisabled() {
      return this.batchStatus === 'Dibatalkan' || !this.isAllShipped
    },
    orderGroups() {
      return (this.items || []).map((order, orderIndex) => {
        const products = order.products?.length ? order.products : [null]

        const rows = products.flatMap((prod, pIdx) => {
          const variants = (prod?.is_bundling && prod.product_bundles?.length)
            ? prod.product_bundles
            : [prod]

          return variants.map((variant, vIdx) => ({
            order,
            orderIndex,
            orderRowspan: 0,
            productRowspan: vIdx === 0 ? variants.length : 0,
            productName: prod?.product_name || '-',
            variantName: variant?.product_variant_name || variant?.product_name || '-',
            totalItem: prod?.total_item ?? '-',
            rowKey: `${order.outbound_detail_id || orderIndex}_${pIdx}_${vIdx}`,
          }))
        })

        if (rows.length) {
          rows[0].orderRowspan = rows.length
        }

        return {
          order,
          orderIndex,
          rows,
        }
      })
    },
    fetcherPackingMaterials() {
      return this.packingMaterialsFetcher?.state?.fetcher
    },
  },
  watch: {
    packingCanBeUpdated(newVal) {
      if (newVal && this.fetcherPackingMaterials?.fetchData) {
        this.fetcherPackingMaterials.fetchData({ reset: true })
      }
    },
    items: {
      handler() {
        this.syncAllOrderSelections(this.selectedOrderIds)
      },
    },
  },
  created() {
    this.packingMaterialsFetcher = usePackingMaterialsFetcher(this)
    this.recalculator = useRecalculateFulfillment(this)
  },
  mounted() {
    if (this.packingMaterialsFetcher) {
      this.packingMaterialsFetcher.initializeFetcherPackingMaterials()
      if (this.packingCanBeUpdated && this.fetcherPackingMaterials?.fetchData) {
        this.fetcherPackingMaterials.fetchData({ reset: true })
      }
    }
  },
  beforeDestroy() {
    if (this.recalculator) {
      this.recalculator.cleanup()
    }
  },
  methods: {
    /**
     * Check if an order row is selectable (not canceled or shipped).
     * @param {Object} item
     * @returns {boolean}
     */
    isSelectableRow(item) {
      return item.status !== 'Dibatalkan' && item.status !== 'Dikirim'
    },
    /**
     * Check if packing selection and additions are disabled for an order.
     * Only selected rows with valid status are enabled when packing can be updated.
     * @param {Object} order
     * @returns {boolean}
     */
    isPackingDisabled(order) {
      return (
        !this.packingCanBeUpdated
        || order.status === 'Dibatalkan'
        || order.status === 'Dikirim'
        || !this.selectedOrderIds.includes(order.outbound_detail_id)
      )
    },
    isAddPackingDisabled(order) {
      if (!order.packings || order.packings.length === 0) return false
      return order.packings.some(p => !p || (!p.id && !p.option_id))
    },
    handleAddPacking(order) {
      if (!order.packings) {
        this.$set(order, 'packings', [])
      }
      order.packings.push({
        id: null,
        option_id: null,
        name: null,
        layer: 1,
      })
    },
    handlePackingChange(order, index) {
      const hasIncomplete = (order.packings || []).some(p => p && (!p.id && !p.option_id))
      if (hasIncomplete) {
        return
      }

      this.recalculator.debouncedRecalculate({
        order,
        packings: order.packings,
        totalOrder: order.total_order,
        orderKey: order.outbound_detail_id,
        onSuccess: result => {
          this.applyRecalculateResult(order, index, result)
        },
      })
    },
    handleRetryRecalculate(order, index) {
      this.recalculator.retryRecalculate(order.outbound_detail_id)
        .then(result => {
          if (result) {
            this.applyRecalculateResult(order, index, result)
          }
        })
        .catch(() => {})
    },
    /**
     * Get the original fulfillment fee for an order before any edits.
     * @param {Object} order
     * @returns {number}
     */
    getOriginalFulfillmentFee(order) {
      return order.fulfillment_fee_per_order ?? order.original_fulfillment_fee ?? order.fulfillment_fee ?? 0
    },
    applyRecalculateResult(order, index, result) {
      const originalFulfillmentFee = this.getOriginalFulfillmentFee(order)
      if (order.fulfillment_fee_per_order === undefined || order.fulfillment_fee_per_order === null) {
        this.$set(order, 'fulfillment_fee_per_order', originalFulfillmentFee)
      }

      const fulfillmentCost = result.fulfillment_cost || result.grand_fulfillment_cost || 0
      this.$set(order, 'fulfillment_fee', fulfillmentCost)

      const mappedItems = {
        outbound_detail_id: order.outbound_detail_id,
        fulfillment_fee: fulfillmentCost,
        total_valid_order: order.total_order,
        kompack_revenue: result.grand_kompack_revenue,
        storage_cost: result.storage_cost,
        packing_cost: result.packing_cost,
        packings: order.packings,
      }

      if (this.outboundOperations && this.outboundOperations.updateSelectedOrder) {
        this.outboundOperations.updateSelectedOrder({
          selected: mappedItems,
          position: index,
          fulfillmentFee: fulfillmentCost,
          originalFulfillmentFee,
        })
      }
    },
    rowClass(item) {
      return (item.status === 'Dibatalkan' || item.status === 'Dikirim') ? 'bg-[#F4F4F4]' : 'hover:bg-[#F9FAFB]'
    },
    toggleSelectRow(order) {
      const key = order.outbound_detail_id
      const selected = [...this.selectedOrderIds]
      const idx = selected.indexOf(key)
      if (idx > -1) {
        selected.splice(idx, 1)
      } else {
        selected.push(key)
      }
      this.selectedOrderIds = selected
      this.handleSelectionChange(selected)
    },
    handleSelectionChange(selectedIds) {
      this.selectedOrderIds = selectedIds
      this.syncAllOrderSelections(selectedIds)
    },
    /**
     * Synchronize all row selection states with outboundOperations.
     * @param {number[]} selectedIds
     */
    syncAllOrderSelections(selectedIds) {
      if (!this.outboundOperations || !this.outboundOperations.updateSelectedOrder) return
      this.items.forEach((item, index) => {
        if (item.status === 'Dibatalkan' || item.status === 'Dikirim') {
          return
        }
        const isSelected = selectedIds.includes(item.outbound_detail_id)
        this.syncOrderSelection(index, isSelected)
      })
    },
    /**
     * Format row payload and sync single row selection state to outboundOperations.
     * @param {number} index
     * @param {boolean} isSelected
     */
    syncOrderSelection(index, isSelected) {
      const item = this.items[index]
      if (!item) return

      const originalFulfillmentFee = this.getOriginalFulfillmentFee(item)
      if (item.fulfillment_fee_per_order === undefined || item.fulfillment_fee_per_order === null) {
        this.$set(item, 'fulfillment_fee_per_order', originalFulfillmentFee)
      }
      const currentFee = item.fulfillment_fee ?? originalFulfillmentFee

      const {
        products, total_order_approved, status, product,
        kompack_revenue: kompackRevenue,
        storage_cost: storageCost,
        packing_cost: packingCost,
        fulfillment_fee_per_order: fulfillmentFeePerOrder,
        total_order: totalOrder,
        ...restItem
      } = item

      const mappedItem = {
        kompack_revenue: kompackRevenue || 0,
        storage_cost: storageCost || 0,
        packing_cost: packingCost || 0,
        ...restItem,
        total_valid_order: totalOrder,
        fulfillment_fee: currentFee,
      }

      this.outboundOperations.updateSelectedOrder({
        type: 'not-packed-yet',
        selected: mappedItem,
        position: index,
        isRemoved: !isSelected,
        fulfillmentFee: currentFee,
        originalFulfillmentFee,
      })
    },
    /**
     * Update packaging materials and fulfillment fee in place and sync with outboundOperations.
     * @param {{ position: number, data: { fulfillment_cost: number, grand_kompack_revenue: number, storage_cost: number, packing_cost: number, packings: Array } }} data
     */
    handleSavePacking(data) {
      const activeItem = this.items[data.position]
      if (!activeItem) return

      const originalFulfillmentFee = this.getOriginalFulfillmentFee(activeItem)
      if (activeItem.fulfillment_fee_per_order === undefined || activeItem.fulfillment_fee_per_order === null) {
        this.$set(activeItem, 'fulfillment_fee_per_order', originalFulfillmentFee)
      }

      this.$set(activeItem, 'fulfillment_fee', data.data.fulfillment_cost)
      this.$set(activeItem, 'packings', data.data.packings)

      const mappedItems = {
        outbound_detail_id: activeItem.outbound_detail_id,
        fulfillment_fee: data.data.fulfillment_cost,
        total_valid_order: activeItem.total_order,
        kompack_revenue: data.data.grand_kompack_revenue,
        storage_cost: data.data.storage_cost,
        packing_cost: data.data.packing_cost,
        packings: data.data.packings,
      }

      this.outboundOperations.updateSelectedOrder({
        selected: mappedItems,
        position: data.position,
        fulfillmentFee: data.data.fulfillment_cost,
        originalFulfillmentFee,
      })
    },
  },
}
</script>
